import { defineComponent, h } from 'vue';
import type { DefineComponent, PropType } from 'vue';

import { DIFFS_TAG_NAME } from '../constants';
import type {
  DiffLineAnnotation,
  FileContents,
  SelectedLineRange,
} from '../types';
import { useUnresolvedFileInstance } from './composables/useUnresolvedFileInstance';
import type { UnresolvedFileOptionsVue, UnresolvedFilePropsVue } from './types';
import { renderDiffChildren } from './utils/renderDiffChildren';
import { templateRender } from './utils/templateRender';

// Renders a file containing Git merge-conflict markers with built-in resolution
// UI. Provide a `#conflict` scoped slot to replace the built-in action buttons
// with your own UI (it receives the conflict `action` and a `resolveConflict`
// callback). Like the React adapter, this component is intentionally
// uncontrolled: `file` seeds the parsed conflict state once; remount (e.g. with
// a changing `key`) to reset.
const UnresolvedFileImpl = defineComponent({
  name: 'PierreUnresolvedFile',
  props: {
    file: { type: Object as PropType<FileContents>, required: true },
    options: {
      type: Object as PropType<UnresolvedFileOptionsVue<undefined>>,
      default: undefined,
    },
    lineAnnotations: {
      type: Array as PropType<DiffLineAnnotation[]>,
      default: undefined,
    },
    selectedLines: {
      type: Object as PropType<SelectedLineRange | null>,
      default: undefined,
    },
    prerenderedHTML: { type: String, default: undefined },
    disableWorkerPool: { type: Boolean, default: false },
  },
  setup(props, { slots }) {
    const { containerRef, state, getHoveredLine, resolveConflict } =
      useUnresolvedFileInstance({
        props,
        hasConflictUtility: () => slots.conflict != null,
        hasCustomHeader: () => slots.header != null,
        hasGutterRenderer: () => slots.gutter != null,
      });
    return () =>
      h(
        DIFFS_TAG_NAME,
        { ref: containerRef },
        templateRender(
          renderDiffChildren({
            fileDiff: state.value.fileDiff,
            slots,
            lineAnnotations: props.lineAnnotations,
            getHoveredLine,
            actions: state.value.actions,
            resolveConflict,
          }),
          props.prerenderedHTML
        )
      );
  },
});

// See the note on `FileDiff` for why this explicit annotation/cast is required.
export const UnresolvedFile =
  UnresolvedFileImpl as unknown as DefineComponent<UnresolvedFilePropsVue>;
